import { t } from "../../utils/I18n";

type LenisLike = { stop?: () => void; start?: () => void };

interface SuggestHit {
    title: string;
    url: string;
    meta?: string;
    thumb?: string;
}

interface SuggestGroup {
    key: string;
    label: string;
    total: number;
    items: SuggestHit[];
}

interface SuggestResponse {
    q: string;
    total: number;
    groups: SuggestGroup[];
}

const RECENT_KEY = "app.search.recent";
const RECENT_MAX = 5;
const DEBOUNCE_MS = 220;
const MIN_QUERY = 2;

export class SearchOverlayController {
    private static overlay: HTMLElement | null = null;
    private static input: HTMLInputElement | null = null;
    private static toggle: HTMLButtonElement | null = null;
    private static form: HTMLFormElement | null = null;
    private static suggestUrl = "/search/suggest";
    private static abortCtrl: AbortController | null = null;
    private static debounceTimer: number | null = null;
    private static activeIndex = -1;

    static init(): void {
        this.overlay = document.querySelector<HTMLElement>("#site-search-overlay");
        this.toggle = document.querySelector<HTMLButtonElement>(".site-header__search");
        if (!this.overlay || !this.toggle) return;

        this.input = this.overlay.querySelector<HTMLInputElement>("[data-search-input]");
        this.form = this.overlay.querySelector<HTMLFormElement>("[data-search-form]");
        this.suggestUrl = this.overlay.dataset.suggestUrl || this.suggestUrl;

        this.toggle.addEventListener("click", () => this.open());

        this.overlay.querySelectorAll<HTMLElement>("[data-search-close]").forEach((el) => {
            el.addEventListener("click", () => this.close());
        });

        this.overlay.querySelector<HTMLButtonElement>("[data-search-clear]")?.addEventListener("click", () => {
            if (!this.input) return;
            this.input.value = "";
            this.input.focus();
            this.handleInput();
        });

        this.input?.addEventListener("input", () => this.handleInput());
        this.input?.addEventListener("keydown", (event) => this.handleKeyDown(event));

        this.form?.addEventListener("submit", (event) => {
            const q = (this.input?.value ?? "").trim();
            if (q === "") {
                event.preventDefault();
                return;
            }
            this.pushRecent(q);
        });

        document.addEventListener("keydown", (event) => {
            if (event.key === "Escape" && this.overlay?.dataset.state === "open") {
                this.close();
            }
        });

        this.renderRecent();
    }

    /* ──────────── Open / Close ──────────── */

    private static open(): void {
        if (!this.overlay) return;
        this.overlay.dataset.state = "open";
        this.overlay.setAttribute("aria-hidden", "false");
        this.toggle?.setAttribute("aria-expanded", "true");
        document.documentElement.classList.add("is-search-open");

        const lenis = (window as unknown as { lenis?: LenisLike }).lenis;
        lenis?.stop?.();

        this.renderRecent();

        // 다음 프레임에 focus (transition 시작 후)
        window.setTimeout(() => this.input?.focus(), 60);
    }

    private static close(): void {
        if (!this.overlay) return;
        this.overlay.dataset.state = "closed";
        this.overlay.setAttribute("aria-hidden", "true");
        this.toggle?.setAttribute("aria-expanded", "false");
        document.documentElement.classList.remove("is-search-open");

        const lenis = (window as unknown as { lenis?: LenisLike }).lenis;
        lenis?.start?.();

        this.activeIndex = -1;

        // 닫힐 때 입력 보존 — 다시 열면 같은 결과 그대로
    }

    /* ──────────── Input handling ──────────── */

    private static handleInput(): void {
        if (!this.input) return;
        const q = this.input.value.trim();

        const clearBtn = this.overlay?.querySelector<HTMLElement>("[data-search-clear]");
        if (clearBtn) {
            if (q === "") clearBtn.setAttribute("hidden", "");
            else clearBtn.removeAttribute("hidden");
        }

        if (this.debounceTimer !== null) {
            window.clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }

        if (q.length < MIN_QUERY) {
            this.showInitial();
            return;
        }

        this.debounceTimer = window.setTimeout(() => {
            this.fetchSuggest(q);
        }, DEBOUNCE_MS);
    }

    private static handleKeyDown(event: KeyboardEvent): void {
        if (!this.overlay) return;
        const hits = Array.from(this.overlay.querySelectorAll<HTMLAnchorElement>(".site-search__hit"));
        if (event.key === "ArrowDown") {
            if (hits.length === 0) return;
            event.preventDefault();
            this.activeIndex = Math.min(hits.length - 1, this.activeIndex + 1);
            this.applyActive(hits);
        } else if (event.key === "ArrowUp") {
            if (hits.length === 0) return;
            event.preventDefault();
            this.activeIndex = Math.max(-1, this.activeIndex - 1);
            this.applyActive(hits);
        } else if (event.key === "Enter") {
            if (this.activeIndex >= 0 && hits[this.activeIndex]) {
                event.preventDefault();
                const q = (this.input?.value ?? "").trim();
                if (q !== "") this.pushRecent(q);
                window.location.href = hits[this.activeIndex].href;
            }
        }
    }

    private static applyActive(hits: HTMLAnchorElement[]): void {
        hits.forEach((el, idx) => el.classList.toggle("is-active", idx === this.activeIndex));
        if (this.activeIndex >= 0) {
            hits[this.activeIndex]?.scrollIntoView({ block: "nearest" });
        }
    }

    /* ──────────── Fetch + render ──────────── */

    private static async fetchSuggest(q: string): Promise<void> {
        if (!this.overlay) return;
        this.showLoading();

        this.abortCtrl?.abort();
        this.abortCtrl = new AbortController();

        try {
            const url = `${this.suggestUrl}?q=${encodeURIComponent(q)}`;
            const res = await fetch(url, {
                signal: this.abortCtrl.signal,
                headers: { Accept: "application/json" },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = (await res.json()) as SuggestResponse;
            this.renderLive(data, q);
        } catch (err) {
            if ((err as DOMException)?.name === "AbortError") return;
            console.warn("[SearchOverlay] suggest failed", err);
            this.showEmpty();
        }
    }

    private static renderLive(data: SuggestResponse, q: string): void {
        if (!this.overlay) return;
        const initial = this.overlay.querySelector<HTMLElement>("[data-search-initial]");
        const live = this.overlay.querySelector<HTMLElement>("[data-search-live]");
        const empty = this.overlay.querySelector<HTMLElement>("[data-search-empty]");
        const loading = this.overlay.querySelector<HTMLElement>("[data-search-loading]");
        const groupsHost = this.overlay.querySelector<HTMLElement>("[data-search-live-groups]");
        const seeAll = this.overlay.querySelector<HTMLAnchorElement>("[data-search-see-all]");

        initial?.setAttribute("hidden", "");
        loading?.setAttribute("hidden", "");

        if (!data.total || !groupsHost) {
            live?.setAttribute("hidden", "");
            empty?.removeAttribute("hidden");
            return;
        }

        empty?.setAttribute("hidden", "");
        live?.removeAttribute("hidden");

        const html = data.groups
            .filter((g) => g.items.length > 0)
            .map((group) => {
                const items = group.items
                    .map(
                        (hit) => `
                            <li>
                                <a class="site-search__hit" href="${escapeAttr(hit.url)}">
                                    <span class="site-search__hit-body">
                                        <span class="site-search__hit-title no-body-md">${escapeHtml(hit.title)}</span>
                                        ${hit.meta ? `<span class="site-search__hit-meta no-body-xs">${escapeHtml(hit.meta)}</span>` : ""}
                                    </span>
                                    ${hit.thumb ? `<img class="site-search__hit-thumb" src="${escapeAttr(hit.thumb)}" alt="" aria-hidden="true" loading="lazy">` : ""}
                                    <i class="fa-regular fa-arrow-right site-search__hit-arrow" aria-hidden="true"></i>
                                </a>
                            </li>`,
                    )
                    .join("");
                const moreLabel = group.total > group.items.length ? t("search.more", { count: group.total - group.items.length }) : `${group.total}`;
                return `
                    <div class="site-search__live-group">
                        <div class="site-search__live-group-head">
                            <p class="site-search__live-group-label no-body-xs --700">${escapeHtml(group.label)}</p>
                            <span class="site-search__live-group-count no-body-xs">${moreLabel}</span>
                        </div>
                        <ul class="site-search__live-list">${items}</ul>
                    </div>`;
            })
            .join("");

        groupsHost.innerHTML = html;

        if (seeAll) {
            const url = new URL(seeAll.href, window.location.origin);
            url.searchParams.set("q", q);
            seeAll.href = url.toString();
        }

        this.activeIndex = -1;
    }

    private static showInitial(): void {
        if (!this.overlay) return;
        this.overlay.querySelector<HTMLElement>("[data-search-initial]")?.removeAttribute("hidden");
        this.overlay.querySelector<HTMLElement>("[data-search-live]")?.setAttribute("hidden", "");
        this.overlay.querySelector<HTMLElement>("[data-search-empty]")?.setAttribute("hidden", "");
        this.overlay.querySelector<HTMLElement>("[data-search-loading]")?.setAttribute("hidden", "");
    }

    private static showLoading(): void {
        if (!this.overlay) return;
        this.overlay.querySelector<HTMLElement>("[data-search-initial]")?.setAttribute("hidden", "");
        this.overlay.querySelector<HTMLElement>("[data-search-live]")?.setAttribute("hidden", "");
        this.overlay.querySelector<HTMLElement>("[data-search-empty]")?.setAttribute("hidden", "");
        this.overlay.querySelector<HTMLElement>("[data-search-loading]")?.removeAttribute("hidden");
    }

    private static showEmpty(): void {
        if (!this.overlay) return;
        this.overlay.querySelector<HTMLElement>("[data-search-initial]")?.setAttribute("hidden", "");
        this.overlay.querySelector<HTMLElement>("[data-search-live]")?.setAttribute("hidden", "");
        this.overlay.querySelector<HTMLElement>("[data-search-loading]")?.setAttribute("hidden", "");
        this.overlay.querySelector<HTMLElement>("[data-search-empty]")?.removeAttribute("hidden");
    }

    /* ──────────── Recent (localStorage) ──────────── */

    private static readRecent(): string[] {
        try {
            const raw = window.localStorage.getItem(RECENT_KEY);
            if (!raw) return [];
            const arr = JSON.parse(raw);
            return Array.isArray(arr) ? arr.filter((s) => typeof s === "string") : [];
        } catch {
            return [];
        }
    }

    private static pushRecent(q: string): void {
        try {
            const list = this.readRecent().filter((s) => s.toLowerCase() !== q.toLowerCase());
            list.unshift(q);
            window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
        } catch {
            /* ignore */
        }
    }

    private static renderRecent(): void {
        if (!this.overlay) return;
        const list = this.readRecent();
        const wrap = this.overlay.querySelector<HTMLElement>("[data-search-recent-wrap]");
        const host = this.overlay.querySelector<HTMLElement>("[data-search-recent]");
        if (!wrap || !host) return;

        if (list.length === 0) {
            wrap.setAttribute("hidden", "");
            host.innerHTML = "";
            return;
        }

        wrap.removeAttribute("hidden");
        const action = this.form?.getAttribute("action") || "/search";
        host.innerHTML = list
            .map(
                (term) => `
                    <li>
                        <a class="ui-badge ui-badge--outline" href="${escapeAttr(action)}?q=${encodeURIComponent(term)}">
                            ${escapeHtml(term)}
                        </a>
                    </li>`,
            )
            .join("");
    }
}

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function escapeAttr(s: string): string {
    return escapeHtml(s);
}
